import type { StatusCategory } from "@sagot/constants";
import type { IconName } from "../../icons";
import { ActionNeededBadge } from "./action-needed-badge";
import { Chip, type ChipSize, type ChipTone } from "./chip";

export type StatusAudience = "staff" | "client";

/** What a status category looks like when its status sets no icon of its own (mockup `D.ticketStatuses`). */
export const STATUS_CATEGORY_CHIP: Readonly<Record<StatusCategory, { tone: ChipTone; icon: IconName }>> = {
  new: { tone: "accent", icon: "sparkles" },
  open: { tone: "info", icon: "loader" },
  pending: { tone: "warning", icon: "hourglass" },
  on_hold: { tone: "muted", icon: "pause-circle" },
  resolved: { tone: "success", icon: "check-circle" },
  closed: { tone: "muted", icon: "archive" },
};

export interface StatusChipProps {
  /** The six fixed categories decide icon and tone; custom statuses only change the words. */
  category: StatusCategory;
  /** The status's staff label ("Waiting for client"). */
  label: string;
  /** What the client reads instead ("Waiting for your reply"); falls back to `label`. */
  clientLabel?: string;
  audience?: StatusAudience;
  /** The status asks the client for something (`pending` statuses). The client sees "Action needed" instead of the label. */
  actionNeeded?: boolean;
  actionNeededLabel?: string;
  /** A status may carry its own icon (the mockup's "calendar" for Visit scheduled). */
  icon?: IconName;
  size?: ChipSize;
  className?: string;
}

/**
 * Ticket status = icon + text + tone from its category. The client audience reads the client label, and where the
 * status waits on them the accent "Action needed" badge (scope 8: the client portal's list).
 */
export function StatusChip({
  category,
  label,
  clientLabel,
  audience = "staff",
  actionNeeded = false,
  actionNeededLabel,
  icon,
  size = "md",
  className,
}: StatusChipProps) {
  const client = audience === "client";
  if (client && actionNeeded && actionNeededLabel) {
    return <ActionNeededBadge label={actionNeededLabel} size={size} className={className} />;
  }
  const spec = STATUS_CATEGORY_CHIP[category];
  const tone = client && category === "pending" ? "accent" : spec.tone;
  return (
    <Chip tone={tone} icon={icon ?? spec.icon} size={size} className={className} data-category={category} data-audience={audience}>
      {client ? (clientLabel ?? label) : label}
    </Chip>
  );
}
